<template>
  <template v-if="isLoginRoute || isFullscreenRoute">
    <router-view />
  </template>
  <MobileView v-else-if="isMobile" />
  <template v-else>
    <DashHeader
      :loading="headerLoading"
      :last-update="headerLastUpdate"
      :countdown="headerCountdown"
      :user="authUser"
      :my-count="headerMyCount"
      :my-open-count="headerMyOpenCount"
      :my-team-filter="headerMyTeamFilter"
      :my-fluxos-filter="headerMyFluxosFilter"
      @export="headerEmitExport"
      @logout="appLogout"
      @test-notif="headerEmitTestNotif"
      @toggle-my-team-filter="headerEmitToggle"
      @toggle-my-fluxos-filter="headerEmitToggleFluxos"
    />
    <div class="app-layout" :class="{ 'sidebar-open': !sidebarCollapsed }">
      <AppSidebar v-model:collapsed="sidebarCollapsed" />
      <div class="app-content">
        <router-view />
      </div>
      <ChatWidget :sidebar-collapsed="sidebarCollapsed" />
      <ConvDrawer />
      <AgentChatDrawer />
    </div>
    <GlobalSearch />
    <ShortcutsHelp />
  </template>
  <!-- Sem excluir isLoginRoute de propósito: beforeinstallprompt costuma disparar
       uma única vez, bem no começo do carregamento da página — como login é a
       primeira tela de quem não está autenticado, excluir essa rota fazia o
       listener nunca estar montado a tempo de capturar o evento, perdendo a
       chance de mostrar o banner depois (nem na própria tela de login nem mais
       tarde no MobileView). SLA Telão (isFullscreenRoute) continua de fora —
       tela de TV/kiosk, sem sentido oferecer instalação ali. -->
  <PwaInstallBanner v-if="isMobile && !isFullscreenRoute" />

  <AutoLogoutWarningModal />
  <ForceLogoutWarningModal />
  <AssignmentAlertModal />
  <BroadcastComposeModal />

  <!-- Janelas de chat flutuantes -->
  <FloatChatWindow
    v-for="win in floatWindows"
    :key="win.uid"
    :win="win"
    @close="floatClose(win.uid)"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import AppSidebar from '@/components/AppSidebar.vue'
import ChatWidget from '@/components/ChatWidget.vue'
import MobileView from '@/views/MobileView.vue'
import PwaInstallBanner from '@/components/PwaInstallBanner.vue'
import FloatChatWindow from '@/components/dashboard/FloatChatWindow.vue'
import ConvDrawer from '@/components/dashboard/ConvDrawer.vue'
import AgentChatDrawer from '@/components/dashboard/AgentChatDrawer.vue'
import DashHeader from '@/components/dashboard/DashHeader.vue'
import GlobalSearch from '@/components/GlobalSearch.vue'
import ShortcutsHelp from '@/components/ShortcutsHelp.vue'
import AutoLogoutWarningModal from '@/components/AutoLogoutWarningModal.vue'
import ForceLogoutWarningModal from '@/components/ForceLogoutWarningModal.vue'
import AssignmentAlertModal from '@/components/AssignmentAlertModal.vue'
import BroadcastComposeModal from '@/components/BroadcastComposeModal.vue'
import { useMobile } from '@/composables/useMobile'
import { useFloatChats } from '@/composables/useFloatChats'
import { useLogo } from '@/composables/useLogo'
import { useAutoLogout, performLogout, continueSession } from '@/composables/useAutoLogout'
import { useForceLogout } from '@/composables/useForceLogout'
import { globalSearchOpen, openGlobalSearch, closeGlobalSearch, toggleGlobalSearch } from '@/composables/useGlobalSearch'
import { shortcutsHelpOpen, toggleShortcutsHelp, closeShortcutsHelp } from '@/composables/useShortcutsHelp'
import { currentOpenConvId, closeDrawer, requestFocusReply, requestOpenMyConvs, goNextConv, goPrevConv, requestOpenChat, requestViewMonthFilter } from '@/composables/useChatTrigger'
import { startDashboardEngine, loadMyTeamIds } from '@/composables/useDashboardEngine'
import { startChatwootRealtime, stopChatwootRealtime } from '@/composables/useChatwootRealtime'
import { startSessionHeartbeat, stopSessionHeartbeat } from '@/composables/useSessionTracking'
import { useAgentChat } from '@/composables/useAgentChat'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { usePermission } from '@/composables/usePermission'
import { useAdmin } from '@/composables/useAdmin'
import { openBroadcastCompose } from '@/composables/useBroadcastCompose'
import { useBroadcast } from '@/composables/useBroadcast'
import { pushAssignmentAlert } from '@/composables/useAssignmentAlert'
import { parseYoutubeId } from '@/utils/youtube'
import type { PendingBroadcast } from '@/types/broadcast'
import {
  headerLoading, headerLastUpdate, headerCountdown,
  headerMyCount, headerMyOpenCount, headerMyTeamFilter, headerMyFluxosFilter,
  headerEmitExport, headerEmitTestNotif, headerEmitToggle, headerEmitToggleFluxos,
} from '@/composables/useHeaderState'

const route  = useRoute()
const router = useRouter()
const store  = useStore()
const sidebarCollapsed  = ref(true)
const isLoginRoute      = computed(() => route.name === 'login')
const isFullscreenRoute = computed(() => route.name === 'sla-metro')
const authUser          = computed(() => store.getters.authUser)
const { isMobile } = useMobile()

const { windows: floatWindows, close: floatClose } = useFloatChats()
const agentChat = useAgentChat()
const { mode: chatRailMode } = useChatRailMode()
const { can } = usePermission()
const { globalSettings } = useAdmin()

// Arma o timer de logout automático por inatividade (login e SLA Telão ficam
// isentos — ver useAutoLogout.ts). Chamado uma única vez aqui: App.vue é a
// raiz da SPA e cobre tanto o layout desktop quanto o MobileView.
useAutoLogout()

// Limpeza do popup de logout forçado ao desmontar — ver useForceLogout.ts.
useForceLogout()

// Arma o motor de dados do Dashboard (conversas, badge "Mês", notificações de
// nova conversa/atribuição, countdown do botão "Atualizar") assim que o login
// completa — ver useDashboardEngine.ts. Precisa ficar aqui (raiz da SPA, nunca
// desmonta) pra continuar rodando mesmo quando o usuário sai da tela do
// Dashboard; antes vivia só em DashboardView.vue e parava ao trocar de rota.
// No mobile (MobileView.vue) não faz sentido: ele já tem seu próprio loadData()
// pras conversas, e o resto do motor (lista completa de /agents, status do
// admin DB, catch-up de call_conv, toggle "Meus Fluxos" do header) é tudo
// UI que só existe no desktop — rodar os dois juntos só duplicava tráfego de
// rede sem nenhum ganho. Só o escopo de times (loadMyTeamIds) é comum aos
// dois — usado por resolveRoleScope() em praticamente toda view, mobile
// incluído — por isso chama ele direto em vez do motor inteiro.
watch(authUser, (user) => {
  if (!user) return
  if (isMobile.value) loadMyTeamIds()
  else startDashboardEngine()
}, { immediate: true })

// Fase 1 do plano de tempo real (docs/planos/PLANO-REALTIME-ACTIONCABLE.md) —
// abre/fecha a conexão ActionCable junto do mesmo ciclo de login/logout do
// resto do app. Ainda não alimenta nenhum estado da dashboard (isso é a
// Fase 2) — só mantém a conexão viva e loga os eventos brutos no console.
watch(authUser, (user) => { if (user) startChatwootRealtime(user); else stopChatwootRealtime() }, { immediate: true })

// Arma o rastreio de sessão (Admin → Sessões, migração v30) — registro +
// heartbeat periódico desta aba, mesmo padrão immediate/watch(authUser) dos
// pollers acima. Ver useSessionTracking.ts.
watch(authUser, (user) => {
  if (user) startSessionHeartbeat(user, store, router)
  else stopSessionHeartbeat()
}, { immediate: true })

// Arma o polling de avisos em massa (tecla K → BroadcastComposeModal) —
// ver useBroadcast.ts. startPolling/stopPolling ficam explícitos (não presos
// ao mount do composable) porque outros consumidores (compositor, futura tela
// de histórico) também chamam useBroadcast() sem controlar esse timer.
const broadcast = useBroadcast()
watch(authUser, (user) => {
  if (user) broadcast.startPolling(user.id)
  else broadcast.stopPolling()
}, { immediate: true })

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

// body/link vêm de texto livre digitado por um agente (compositor) — escapa
// antes de injetar no `html` do Swal.fire pra não abrir XSS armazenado entre
// agentes. youtubeId não precisa de escape: já sai filtrado pelo regex de
// parseYoutubeId ([\w-]{11}), não carrega HTML/aspas.
function buildBroadcastHtml(b: PendingBroadcast): string {
  const parts = [
    `<div style="text-align:left;white-space:pre-wrap;font-size:14px;line-height:1.5;margin-bottom:12px;">${escapeHtml(b.body)}</div>`,
  ]
  if (b.imageUrl) {
    parts.push(`<img src="${escapeHtml(b.imageUrl)}" style="max-width:100%;max-height:220px;border-radius:8px;object-fit:contain;margin-bottom:10px;" />`)
  }
  const ytId = b.youtubeUrl ? parseYoutubeId(b.youtubeUrl) : null
  if (ytId) {
    parts.push(`<iframe src="https://www.youtube.com/embed/${ytId}" style="width:100%;aspect-ratio:16/9;border:0;border-radius:8px;margin-bottom:10px;" allowfullscreen></iframe>`)
  }
  if (b.linkUrl) {
    parts.push(`<a href="${escapeHtml(b.linkUrl)}" target="_blank" rel="noopener noreferrer" style="color:#BC0C24;font-weight:600;word-break:break-all;">${escapeHtml(b.linkUrl)}</a>`)
  }
  return parts.join('')
}

// Guarda por id (não só "existe currentBroadcast") porque fetchPendingBroadcasts
// devolve um objeto NOVO a cada poll de 15s mesmo pro mesmo aviso ainda não
// confirmado — sem essa guarda o watch reabriria o Swal a cada ciclo enquanto
// o agente ainda está com o modal aberto.
let showingBroadcastId: number | null = null
watch(() => broadcast.currentBroadcast.value, async (b) => {
  if (!b || showingBroadcastId === b.id) return
  showingBroadcastId = b.id
  await Swal.fire({
    title: 'Aviso',
    html: buildBroadcastHtml(b),
    confirmButtonText: 'Confirmado',
    confirmButtonColor: '#BC0C24',
    allowOutsideClick: false,
    allowEscapeKey: false,
  })
  await broadcast.ack(b.id)
  showingBroadcastId = null
})

function onKeydown(e: KeyboardEvent) {
  // Ctrl+K / Cmd+K — sempre
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    toggleGlobalSearch()
    return
  }

  // Ctrl+! — só preview visual do popup "Novo atendimento atribuído"
  // (AssignmentAlertModal), não atribui nada de verdade. ConvId sintético
  // (negativo, timestamp) pra nunca colidir com uma conversa real e sempre
  // empilhar uma nova prévia mesmo repetindo o atalho.
  if ((e.ctrlKey || e.metaKey) && e.key === '!') {
    e.preventDefault()
    pushAssignmentAlert({ convId: -Date.now(), contactName: 'Cliente de Teste' })
    return
  }

  // Esc — fecha o que estiver aberto
  if (e.key === 'Escape') {
    if (shortcutsHelpOpen.value)            { closeShortcutsHelp(); return }
    if (globalSearchOpen.value)             { closeGlobalSearch();  return }
    if (currentOpenConvId.value !== null)   { closeDrawer();        return }
    return
  }

  // Atalhos de uma tecla nunca disparam com modificador — senão Ctrl+C pra copiar
  // texto selecionado (e.key continua 'c' com Ctrl segurado) abriria "Minhas
  // Conversas" em vez de copiar.
  if (e.ctrlKey || e.metaKey || e.altKey) return

  // Atalhos de uma tecla: não disparar dentro de inputs/textareas
  const tag = (document.activeElement?.tagName ?? '').toUpperCase()
  const editing = tag === 'INPUT' || tag === 'TEXTAREA' ||
    (document.activeElement as HTMLElement | null)?.isContentEditable
  if (editing) return

  switch (e.key) {
    case '/':
      e.preventDefault()
      openGlobalSearch()
      break
    case '?':
      e.preventDefault()
      toggleShortcutsHelp()
      break
    case 'n': case 'N':
      e.preventDefault()
      goNextConv()
      break
    case 'p': case 'P':
      e.preventDefault()
      goPrevConv()
      break
    case 'r': case 'R':
      e.preventDefault()
      requestFocusReply()
      break
    case 'c': case 'C':
      e.preventDefault()
      requestOpenMyConvs()
      break
    case 'k': case 'K':
      if (!can('broadcast.send') || !globalSettings.value.broadcastEnabled) return
      e.preventDefault()
      openBroadcastCompose()
      break
    case 'm': case 'M':
      if (!can('foco.view') || route.name === 'foco') return
      e.preventDefault()
      router.push({ name: 'foco' })
      break
  }
}

const { loadLogo } = useLogo()

// Abre a conversa indicada por uma notificação clicada — seja via aba nova
// (query param ?conv=) ou aba já existente (mensagem do service worker),
// nunca navegando para o Chatwoot.
function onSWMessage(e: MessageEvent) {
  if (e.data?.type === 'notif-open-conv' && e.data.convId) {
    requestOpenChat(Number(e.data.convId))
  } else if (e.data?.type === 'notif-open-agent-chat' && e.data.senderId) {
    chatRailMode.value = 'agent'
    agentChat.panelOpen.value = true
    agentChat.openThread(authUser.value?.id ?? 0, Number(e.data.senderId))
  } else if (e.data?.type === 'notif-continue-session') {
    continueSession(store)
  } else if (e.data?.type === 'notif-view-month-filter') {
    requestViewMonthFilter()
    if (route.name !== 'dashboard') router.push({ name: 'dashboard' })
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  loadLogo()

  const convParam = route.query.conv
  if (convParam) {
    requestOpenChat(Number(convParam))
    const { conv, ...rest } = route.query
    router.replace({ query: rest })
  }

  navigator.serviceWorker?.addEventListener('message', onSWMessage)
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  navigator.serviceWorker?.removeEventListener('message', onSWMessage)
})

async function appLogout() {
  await performLogout(store, router)
}
</script>

<style>
.app-layout {
  display: flex;
  min-height: 100vh;
}

.app-content {
  flex: 1;
  min-width: 0;
  margin-left: 64px;
  margin-right: 60px;
  transition: margin-left 0.26s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-layout.sidebar-open .app-content {
  margin-left: 220px;
}
</style>
